import { HelpOutline, UploadFile } from '@mui/icons-material';
import {
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { IDocument } from '../Interfaces/IDocument';
import { IDocumentDownloaded } from '../Interfaces/IDocumentDownloaded';
import { IMIMEType } from '../Interfaces/IMIMEType';
import { State } from '../redux/mainReducer';
import { DocumentRow } from './DocumentRow';
import { ILegalClient } from '../Interfaces/ILegalClient';

interface IProps {
  documentArray: IDocument[];
  setDocumentArray: (newDocuments: IDocument[]) => void;
  allowUpload: boolean;
  allowRemove: boolean;
  name?: string;
  title: string;
  downloadedArray?: IDocumentDownloaded[];
  children?: React.ReactElement;
  titleVariant?: 'h4' | 'h5' | 'h6' | 'subtitle1';
  marginTop?: number;
  enableSidepanel?: boolean;
  isStandarddocument?: boolean;
  isTemplate?: boolean;
  isSendEmailPossible?: boolean;
  checkedBoxes?: number[];
  setCheckedBoxes?: React.Dispatch<React.SetStateAction<number[]>>;
  legalClients?: ILegalClient[];
}

export async function fileToBase64(file: File) {
  const result_base64 = await new Promise(resolve => {
    const fileReader = new FileReader();
    fileReader.onload = () => resolve(fileReader.result);
    fileReader.readAsDataURL(file);
  });
  return String(result_base64).split(',')[1];
}

export const DocumentOverview: React.FC<IProps> = ({
  documentArray,
  setDocumentArray,
  allowUpload,
  allowRemove,
  name = '',
  title,
  downloadedArray,
  children,
  titleVariant,
  marginTop,
  enableSidepanel = false,
  isStandarddocument = false,
  isTemplate = false,
  isSendEmailPossible = true,
  checkedBoxes,
  setCheckedBoxes,
  legalClients,
}) => {
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );
  const [enableUpload, setEnableUpload] = useState(false);
  const inputUpload = useRef<HTMLInputElement | null>(null);

  const handleFilesUpload = async (acceptedFiles: File[]) => {
    const uploadArray: IDocument[] = [];
    let minIdOffset = -1;

    if (documentArray.length > 0) {
      const minIdArray = Math.min(
        ...documentArray.map(document => document.idDocument)
      );

      if (minIdArray <= minIdOffset) {
        minIdOffset = minIdArray - 1;
      }
    }

    let index = 0;

    for (const currentFile of acceptedFiles) {
      const fileName: string = String(currentFile['name']);
      const mimeType: IMIMEType | undefined = mimeTypeArray.find(
        mimeType => mimeType.MIMEType === currentFile['type']
      );

      if (mimeType) {
        uploadArray.push({
          idDocument: minIdOffset - index,
          FileName: fileName,
          idMIMEType: mimeType.idMIMEType,
          Data: await fileToBase64(currentFile),
          Created_at: new Date().toISOString(),
          isFromTemplate: isTemplate,
        } as IDocument);
      }
      index = index + 1;
    }

    setDocumentArray([...documentArray, ...uploadArray]);
  };

  const handleNormalUpload = (files: null | FileList) => {
    if (files) {
      const acceptedFiles: File[] = Array.from(files);
      handleFilesUpload(acceptedFiles);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleFilesUpload(acceptedFiles);
      setEnableUpload(false);
    },
    [documentArray]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
  });

  const dropZoneTSX = () => {
    return (
      <div>
        <input
          {...getInputProps()}
          accept={String(
            mimeTypeArray.map(mimeType => mimeType.MIMEType).join(', ')
          )}
        />
        <Card>
          <CardContent>
            <Typography variant='body2'>Dokument(e) hier reinziehen</Typography>
            Erlaubte Datentypen
            <br />
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Datentyp</TableCell>
                  <TableCell>Datenendung</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mimeTypeArray.map(mimeType => (
                  <TableRow>
                    <TableCell>{mimeType.MIMEType}</TableCell>
                    <TableCell>{mimeType.FileExtension}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div {...getRootProps()}>
      <input
        hidden
        ref={inputUpload}
        type='file'
        accept={String(
          mimeTypeArray.map(mimeType => mimeType.MIMEType).join(', ')
        )}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleNormalUpload(event.target.files)
        }
      />

      {name !== '' && (
        <Typography mb={2} variant={'h5'}>
          {' '}
          {name}
        </Typography>
      )}

      <Typography
        variant={titleVariant || 'h5'}
        sx={{ mb: 2, mt: marginTop || 0 }}
      >
        {title}
        {allowUpload && (
          <IconButton
            title='Daten über Auswähl hochladen'
            onClick={() => inputUpload && inputUpload.current?.click()}
            sx={{ float: 'right' }}
          >
            <UploadFile />
          </IconButton>
        )}
        {allowUpload && (
          <>
            <br />
            <Typography variant='caption'>
              (Hier können Sie Ihre Dokumente per <i>Drag & Drop</i> hochladen)
            </Typography>
          </>
        )}
      </Typography>

      {(isDragActive || enableUpload) && allowUpload && dropZoneTSX()}

      {!children ? (
        <>
          <Table>
            <TableHead>
              {isStandarddocument ? (
                <TableRow>
                  {setCheckedBoxes && <TableCell />}
                  <TableCell width={'10%'}>Nr.</TableCell>
                  {downloadedArray && (
                    <TableCell width={'10%'}>Heruntergeladen</TableCell>
                  )}
                  <TableCell width={downloadedArray ? '30%' : '40%'}>
                    Dateiname
                  </TableCell>
                  <TableCell width={downloadedArray ? '10%' : '10%'}>
                    Standarddokument
                  </TableCell>
                  <TableCell width={downloadedArray ? '20%' : '20%'}>
                    Als E-Mail Anhang&nbsp;
                    <Tooltip
                      title={
                        isSendEmailPossible
                          ? 'Wenn ausgewählt, wird das Dokument beim Verschicken der E-Mail als Anhang hinzugefügt.'
                          : 'Es kann aktuell keine E-Mail verschickt werden.'
                      }
                      arrow
                    >
                      <HelpOutline
                        fontSize='small'
                        color='action'
                        style={{ verticalAlign: 'middle' }}
                      />
                    </Tooltip>
                  </TableCell>

                  <TableCell width={downloadedArray ? '20%' : '20%'}>
                    Aktionen
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  {checkedBoxes && setCheckedBoxes && <TableCell />}
                  <TableCell width={'10%'}>Nr.</TableCell>
                  {downloadedArray && (
                    <TableCell width={'20%'}>Heruntergeladen</TableCell>
                  )}
                  <TableCell width={downloadedArray ? '50%' : '60%'}>
                    Dateiname
                  </TableCell>
                  <TableCell width={downloadedArray ? '20%' : '30%'}>
                    Aktionen
                  </TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {documentArray &&
                documentArray.map(document => (
                  <DocumentRow
                    key={`idDocument-${document.idDocument}`}
                    documentObject={document}
                    documentArray={documentArray}
                    setDocumentArray={setDocumentArray}
                    mimeTypeArray={mimeTypeArray}
                    allowRemove={allowRemove}
                    downloadedArray={downloadedArray}
                    enableSidepanel={enableSidepanel}
                    isStandarddocument={isStandarddocument}
                    isSendEmailPossible={isSendEmailPossible}
                    checkedBoxes={checkedBoxes}
                    setCheckedBoxes={setCheckedBoxes}
                    assignedLegalClients={legalClients || []}
                  />
                ))}
            </TableBody>
          </Table>
        </>
      ) : (
        <>{children}</>
      )}
    </div>
  );
};
